import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthGuard } from '@/components/AuthGuard';
import { Toaster } from 'sonner';
import { LayoutDashboard, ShoppingBag, Plus, LogOut, Settings, ChevronRight, ChevronDown, User, Moon, Sun, Menu, Library, Globe, MousePointerClick, PackageSearch, Search, ShieldAlert, NotebookPen, Users, Ticket, BarChart3, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n';

const ANALYTICS_PATHS = [
  '/admin/analytics/agents',
  '/admin/analytics/products',
  '/admin/analytics/discovery',
  '/admin/analytics/reliability',
];

const CREATORS_PATHS = [
  '/admin/creators',
  '/admin/analytics/creators',
];

export function AdminLayout() {
  const { user, role, isOwner, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(() =>
    ANALYTICS_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
  );
  const [creatorsOpen, setCreatorsOpen] = useState(() =>
    CREATORS_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
  );
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
    } else {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
    }
  }, []);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Keep groups expanded when navigating into their routes
  useEffect(() => {
    if (ANALYTICS_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))) {
      setAnalyticsOpen(true);
    }
    if (CREATORS_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))) {
      setCreatorsOpen(true);
    }
  }, [location.pathname]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
      localStorage.setItem('theme', 'dark');
      toast.success(t('darkModeEnabled'));
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
      localStorage.setItem('theme', 'light');
      toast.success(t('lightModeEnabled'));
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleLanguage = () => {
    const next = lang === 'en' ? 'zh' : 'en';
    setLang(next);
    toast.success(next === 'en' ? t('languageEnglish') : t('languageChinese'));
  };

  const navLinkClass = (active: boolean) =>
    `group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
      active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-foreground hover:bg-primary/10'
    }`;

  const childLinkClass = (active: boolean) =>
    `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-foreground hover:bg-primary/10'
    }`;

  return (
    <AuthGuard>
      <div className={`min-h-screen bg-background flex flex-col ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center gap-2 px-4">
            <button onClick={toggleSidebar} className="-ml-2 rounded-md p-1.5 text-muted-foreground hover:bg-secondary/80 transition-colors lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/admin" className="flex items-center gap-2 font-semibold">
              <div className="bg-primary rounded-md p-1.5 text-primary-foreground">
                <Library size={18} />
              </div>
              <span className="font-semibold text-xl">{t('panelTitle')}</span>
            </Link>
            <div className="flex items-center ml-auto gap-2">
              <div className="flex items-center gap-2 mr-2">
                <Button variant="ghost" size="icon" onClick={toggleLanguage} className="relative hover:bg-secondary/80 transition-colors">
                  <Globe size={18} />
                  <span className="sr-only">Toggle language</span>
                </Button>
                <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="relative hover:bg-secondary/80 transition-colors">
                  {isDarkMode ? <Moon size={18} /> : <Sun size={18} />}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          <aside 
            className={`fixed inset-y-0 left-0 z-30 mt-16 overflow-y-auto bg-card border-r transition-all duration-300 
              ${isMobile ? (sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full') : 'translate-x-0 w-64 md:w-52 lg:w-64'}`}
          >
            <div className="py-4">
              <div className="px-4 py-2">
                <div className="flex items-center gap-2 py-3 mb-2 border-b border-border pb-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <User size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate">{user?.email}</span>
                    <span className="text-xs text-muted-foreground">{role === 'worker' ? t('roleWorker') : t('roleOwner')}</span>
                  </div>
                </div>
              </div>
              <nav className="mt-2 px-2 space-y-1">
                <Link
                  to="/admin"
                  className={navLinkClass(isActive('/admin'))}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <LayoutDashboard className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">{t('navDashboard')}</span>
                  {isActive('/admin') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>

                <Link
                  to="/admin/operations"
                  className={navLinkClass(isActive('/admin/operations'))}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <Wrench className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">{t('navOperations')}</span>
                  {isActive('/admin/operations') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>

                {/* Analytics group (excludes creator analytics) */}
                <div>
                  <button
                    type="button"
                    onClick={() => setAnalyticsOpen((open) => !open)}
                    className="w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md text-foreground hover:bg-primary/10 transition-colors"
                    aria-expanded={analyticsOpen}
                  >
                    <BarChart3 className="mr-3 h-5 w-5" />
                    <span className="whitespace-nowrap">{t('navAnalyticsGroup')}</span>
                    {analyticsOpen
                      ? <ChevronDown className="ml-auto h-4 w-4 opacity-70" />
                      : <ChevronRight className="ml-auto h-4 w-4 opacity-70" />}
                  </button>
                  {analyticsOpen && (
                    <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                      <Link
                        to="/admin/analytics/agents"
                        className={childLinkClass(isActive('/admin/analytics/agents'))}
                        onClick={() => isMobile && setSidebarOpen(false)}
                      >
                        <MousePointerClick className="mr-3 h-4 w-4" />
                        <span className="whitespace-nowrap">{t('navAnalyticsAgents')}</span>
                      </Link>
                      <Link
                        to="/admin/analytics/products"
                        className={childLinkClass(isActive('/admin/analytics/products'))}
                        onClick={() => isMobile && setSidebarOpen(false)}
                      >
                        <PackageSearch className="mr-3 h-4 w-4" />
                        <span className="whitespace-nowrap">{t('navAnalyticsProducts')}</span>
                      </Link>
                      <Link
                        to="/admin/analytics/discovery"
                        className={childLinkClass(isActive('/admin/analytics/discovery'))}
                        onClick={() => isMobile && setSidebarOpen(false)}
                      >
                        <Search className="mr-3 h-4 w-4" />
                        <span className="whitespace-nowrap">{t('navAnalyticsDiscovery')}</span>
                      </Link>
                      <Link
                        to="/admin/analytics/reliability"
                        className={childLinkClass(isActive('/admin/analytics/reliability'))}
                        onClick={() => isMobile && setSidebarOpen(false)}
                      >
                        <ShieldAlert className="mr-3 h-4 w-4" />
                        <span className="whitespace-nowrap">{t('navAnalyticsReliability')}</span>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Creators group */}
                <div>
                  <button
                    type="button"
                    onClick={() => setCreatorsOpen((open) => !open)}
                    className="w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md text-foreground hover:bg-primary/10 transition-colors"
                    aria-expanded={creatorsOpen}
                  >
                    <Users className="mr-3 h-5 w-5" />
                    <span className="whitespace-nowrap">{t('navCreatorsGroup')}</span>
                    {creatorsOpen
                      ? <ChevronDown className="ml-auto h-4 w-4 opacity-70" />
                      : <ChevronRight className="ml-auto h-4 w-4 opacity-70" />}
                  </button>
                  {creatorsOpen && (
                    <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                      {isOwner && (
                        <Link
                          to="/admin/creators"
                          className={childLinkClass(isActive('/admin/creators'))}
                          onClick={() => isMobile && setSidebarOpen(false)}
                        >
                          <Ticket className="mr-3 h-4 w-4" />
                          <span className="whitespace-nowrap">{t('navCreators')}</span>
                        </Link>
                      )}
                      <Link
                        to="/admin/analytics/creators"
                        className={childLinkClass(isActive('/admin/analytics/creators'))}
                        onClick={() => isMobile && setSidebarOpen(false)}
                      >
                        <Users className="mr-3 h-4 w-4" />
                        <span className="whitespace-nowrap">{t('navAnalyticsCreators')}</span>
                      </Link>
                    </div>
                  )}
                </div>

                {isOwner && (
                  <>
                    <Link
                      to="/admin/search-suggestions"
                      className={navLinkClass(isActive('/admin/search-suggestions'))}
                      onClick={() => isMobile && setSidebarOpen(false)}
                    >
                      <Search className="mr-3 h-5 w-5" />
                      <span className="whitespace-nowrap">{t('navSearchSuggestions')}</span>
                      {isActive('/admin/search-suggestions') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                    </Link>

                    <Link
                      to="/admin/clothinglist"
                      className={navLinkClass(isActive('/admin/clothinglist'))}
                      onClick={() => isMobile && setSidebarOpen(false)}
                    >
                      <ShoppingBag className="mr-3 h-5 w-5" />
                      <span className="whitespace-nowrap">{t('navDb')}</span>
                      {isActive('/admin/clothinglist') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                    </Link>

                    <Link
                      to="/admin/clothinglist/new"
                      className={navLinkClass(isActive('/admin/clothinglist/new'))}
                      onClick={() => isMobile && setSidebarOpen(false)}
                    >
                      <Plus className="mr-3 h-5 w-5" />
                      <span className="whitespace-nowrap">{t('navAddProduct')}</span>
                      {isActive('/admin/clothinglist/new') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                    </Link>
                  </>
                )}

                <Link
                  to="/admin/notes"
                  className={navLinkClass(isActive('/admin/notes'))}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <NotebookPen className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">{t('navNotes')}</span>
                  {isActive('/admin/notes') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                </Link>

                {isOwner && (
                  <Link
                    to="/admin/settings"
                    className={navLinkClass(isActive('/admin/settings'))}
                    onClick={() => isMobile && setSidebarOpen(false)}
                  >
                    <Settings className="mr-3 h-5 w-5" />
                    <span className="whitespace-nowrap">{t('settings')}</span>
                    {isActive('/admin/settings') && <ChevronRight className="ml-auto h-4 w-4 opacity-100 transition-opacity" />}
                  </Link>
                )}
                
                <div className="pt-4 mt-4 border-t border-border">
                  <button
                    onClick={() => signOut()}
                    className="w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md text-foreground hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    <span className="whitespace-nowrap">{t('signOut')}</span>
                  </button>
                </div>
              </nav>
            </div>
          </aside>

          <main className={`flex-1 overflow-y-auto pt-16 transition-all duration-300 ${isMobile ? '' : 'md:ml-52 lg:ml-64'}`}>
            <div className="container mx-auto px-6 py-6">
              <Outlet />
            </div>
          </main>
        </div>
        <Toaster />
      </div>
    </AuthGuard>
  );
}
